import { z } from 'zod';
import { api } from '@/utils/api';

export interface DashboardStage {
  id: string;
  name: string;
  color?: string;
  order?: number;
}

interface PipelineLike {
  id?: string;
  stages?: Array<{ id: string; name?: string; color?: string; order?: number }>;
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Flatten `GET /deals/pipelines` into the dashboard's Stage[] (order-sorted). */
export function flattenPipelineStages(pipelines: PipelineLike[] | null | undefined): DashboardStage[] {
  if (!Array.isArray(pipelines)) return [];
  const out: DashboardStage[] = [];
  for (const p of pipelines) {
    if (!p || !Array.isArray(p.stages)) continue;
    for (const s of p.stages) {
      if (!s || typeof s.id !== 'string') continue;
      out.push({ id: s.id, name: s.name ?? '', color: s.color, order: s.order });
    }
  }
  out.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return out;
}

/** Read pipeline value + open-deal count from the FLAT owner-dashboard payload. */
export function pipelineFromOwnerDashboard(
  owner: Record<string, unknown> | null | undefined,
): { value: number | null; count: number } {
  if (!owner || typeof owner !== 'object') return { value: null, count: 0 };
  const value = num((owner as Record<string, unknown>)['sales.pipeline_value']);
  const count = num((owner as Record<string, unknown>)['sales.active_deals']) ?? 0;
  return { value, count };
}

/** Read the real Lead→Won win-rate (0–100). Out-of-range/absent → null. */
export function winRateFromOwnerDashboard(owner: Record<string, unknown> | null | undefined): number | null {
  if (!owner || typeof owner !== 'object') return null;
  const wr = num((owner as Record<string, unknown>)['sales.win_rate']);
  if (wr === null || wr < 0 || wr > 100) return null;
  return wr;
}


const amount = z.number().finite().nonnegative();
const count = amount.int();
const name = z.string().trim().min(1).refine(value => value !== 'Unnamed');
const date = z.string().datetime({ offset: true });
const totals = z.object({ value: amount, count, unvalued: count });
const teamSalesSchema = z.object({
  period: name,
  pipeline: totals,
  won: z.object({ value: amount.nullable(), count }),
  conversion: amount.max(100).nullable(),
  stages: z.array(z.object({ id: name, name, value: amount.nullable(), count, unvalued: count, period: z.enum(['month', 'all']).optional() })),
  reps: z.array(z.object({
    id: name, name, openDeals: count, won: amount.nullable(), targetPct: amount.nullable(),
    callsToday: count, overdue: count, incomplete: z.boolean().optional(), targetMissing: z.boolean().optional(), targetsNotSetUp: z.array(z.string()).optional(),
  })),
  approvals: z.array(z.object({
    id: name, document: name, customer: z.string().nullable(), requester: name,
    requestedPct: amount.max(100), item: z.string().nullable(),
  })),
  overdue: z.array(z.object({ id: name, lead: name, owner: name, due: date, stage: name, lastTouch: date.nullable() })),
});
export type TeamSalesData = z.infer<typeof teamSalesSchema>;

export async function fetchTeamSales(): Promise<TeamSalesData> {
  const response = await api.get('/analytics/sales/stage-conversion?view=team-dashboard&scope=TEAM');
  if (response.data?.success !== true) throw new Error('Sales query failed');
  return teamSalesSchema.parse(response.data.data?.meta?.teamSales);
}
